import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import BackIcon from "../../components/back-icon"
import { Link } from "react-router-dom"
import { useNavigate } from "react-router-dom"

// Define API response type
interface Specialty {
  id: number
  speciality: string
  orderId: number
}

// Fetch specialties from API
const fetchSpecialties = async (): Promise<Specialty[]> => {
  const response = await fetch(`${process.env.REACT_APP_API_URL}/hpr-hfr-api/api/onboarding/all-specialities`)
  if (!response.ok) {
    throw new Error("Failed to fetch specialties")
  }
  return response.json()
}

// Define prop types for SpecialtyItem
interface SpecialtyItemProps {
  label: string
  onClick: () => void
}

// Specialty Item Component
const SpecialtyItem = ({ label, onClick }: SpecialtyItemProps) => (
  <button
  onClick={onClick}
  className="group flex flex-col items-center justify-center p-4 bg-gradient-to-br from-[#E6F0FA] to-[#F8FBFF] rounded-xl hover:from-[#D1E3F6] hover:to-[#E6F0FA] transition-all duration-300 shadow-sm hover:shadow-md border border-[#E1EBF4] hover:border-[#C5D9E8] transform hover:-translate-y-1 h-20"
>
    <span className="text-sm font-semibold text-center text-gray-800 group-hover:text-[#0F676C] transition-colors duration-200 leading-tight">
  {label}
</span>
  </button>
)

// Define prop types for SpecialtyList
interface SpecialtyListProps {
  onSelectSpecialty: (specialty: string) => void
}

const SpecialtyList = ({ onSelectSpecialty }: SpecialtyListProps) => {
  const [showAll, setShowAll] = useState(false)

  // Fetch specialties using react-query
  const {
    data: specialties = [],
    isLoading,
    isError,
  } = useQuery<Specialty[]>({
    queryKey: ["specialties"],
    queryFn: fetchSpecialties,
  })

  // Sort specialties by orderId
  const sortedSpecialties = specialties.sort((a, b) => a.orderId - b.orderId)

  // Show first 7 specialties by default, or all if showAll is true
  const visibleSpecialties = showAll ? sortedSpecialties : sortedSpecialties.slice(0, window.innerWidth >= 768 ? 17 : 7)
  if (isLoading) {
    return (
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-12 md:pt-4 lg:pt-2">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-xl sm:text-2xl font-bold mb-8 text-gray-900 text-center sm:text-left">
            Find a Specialist
          </h2>
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#E6F0FA] border-t-[#0F676C]"></div>
          </div>
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-12 md:pt-4 lg:pt-2">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-xl sm:text-2xl font-bold mb-8 text-gray-900 text-center sm:text-left">
            Find a Specialist
          </h2>
          <div className="text-center py-12">
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 max-w-md mx-auto">
              <p className="text-red-600 font-medium">Failed to load specialties.</p>
              <p className="text-red-500 text-sm mt-1">Please try again later.</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-11 mt-2 sm:pt-14 md:pt-4 lg:pt-2 pb-3">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-start sm:justify-start gap-3 md:gap-4 mb-6 sm:mb-7">
          {localStorage.getItem("mobile") && (
            <Link
              to={`/doctors`}
              className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors duration-200 group"
            >
              <BackIcon />
              <span className="absolute left-1/2 top-full mt-1 hidden -translate-x-1/2 items-center rounded bg-gray-800 p-1 text-xs text-white shadow-lg group-hover:flex">
                Back
              </span>
            </Link>
          )}
          <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Find a Specialist</h3>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-x-4 gap-y-6 sm:gap-x-6 sm:gap-y-8">
          {visibleSpecialties.map((specialty) => (
            <SpecialtyItem
              key={specialty.id}
              label={specialty.speciality}
              onClick={() => onSelectSpecialty(specialty.speciality)}
            />
          ))}
          {!showAll && sortedSpecialties.length > 7 && (
            <button
            onClick={() => setShowAll(true)}
            className="group flex flex-col items-center justify-center p-4 bg-gradient-to-br from-[#0F676C] to-[#17888D] rounded-xl hover:from-[#17888D] hover:to-[#1F9BA1] transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-1 border border-[#0F676C] h-20"
          >
              <span className="text-2xl sm:text-3xl font-bold text-white mb-1 group-hover:scale-110 transition-transform duration-200">
  {sortedSpecialties.length - (window.innerWidth >= 768 ? 17 : 7)}+
</span>
              <span className="text-xs sm:text-sm font-semibold text-center text-white/90 group-hover:text-white transition-colors duration-200">
                See More
              </span>
            </button>
          )}
        </div>

        {showAll && (
          <div className="text-center mt-8">
            <button
              onClick={() => setShowAll(false)}
              className="inline-flex items-center px-6 py-3 bg-white border-2 border-[#0F676C] text-[#0F676C] rounded-xl hover:bg-[#0F676C] hover:text-white transition-all duration-300 font-semibold text-sm shadow-sm hover:shadow-md"
            >
              Show Less
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default SpecialtyList
